import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { DatePicker } from "@devhop/ui";
import { formatDateForInput } from "@/shared/lib/date-time";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import type { LookupItem } from "@/shared/lib/utils";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { config } from "@/shared/lib/config";

export type MeetingActionsSearchParams = {
  offset?: number;
  limit?: number;
  sort?: Array<{ field: string; dir: "asc" | "desc" }>;
  dueDateFrom?: string;
  dueDateTo?: string;
  status?: "pending" | "done";
  assigneeId?: string;
  meetingId?: string;
};

type MeetingActionsFilterProps = {
  searchParams: MeetingActionsSearchParams;
  fromRoute: "/app/management/meetings/actions";
};

const STATUS_ALL = "_all";

const STATUS_OPTIONS = [
  { value: STATUS_ALL, label: "ທັງໝົດ" },
  { value: "pending", label: "ລໍຖ້າ" },
  { value: "done", label: "ສຳເລັດ" },
];

export function MeetingActionsFilter({
  searchParams,
  fromRoute,
}: MeetingActionsFilterProps) {
  const nav = useNavigate({ from: fromRoute });
  const [dueDateFrom, setDueDateFrom] = useState(searchParams.dueDateFrom ?? "");
  const [dueDateTo, setDueDateTo] = useState(searchParams.dueDateTo ?? "");
  const [status, setStatus] = useState(searchParams.status ?? STATUS_ALL);
  const [assigneeId, setAssigneeId] = useState(searchParams.assigneeId ?? "");

  useEffect(() => {
    setDueDateFrom(searchParams.dueDateFrom ?? "");
    setDueDateTo(searchParams.dueDateTo ?? "");
    setStatus(searchParams.status ?? STATUS_ALL);
    setAssigneeId(searchParams.assigneeId ?? "");
  }, [
    searchParams.dueDateFrom,
    searchParams.dueDateTo,
    searchParams.status,
    searchParams.assigneeId,
  ]);

  const doNav = (
    opts: Partial<{
      dueDateFromVal: string;
      dueDateToVal: string;
      statusVal: string;
      assigneeIdVal: string;
      meetingIdVal: string;
    }> = {},
  ) => {
    const df = opts.dueDateFromVal ?? dueDateFrom;
    const dt = opts.dueDateToVal ?? dueDateTo;
    const st = opts.statusVal ?? status;
    const aid = opts.assigneeIdVal ?? assigneeId;
    const mid = opts.meetingIdVal ?? searchParams.meetingId;
    nav({
      search: {
        offset: 0,
        limit: searchParams.limit ?? 20,
        sort: searchParams.sort,
        dueDateFrom: df.trim() || undefined,
        dueDateTo: dt.trim() || undefined,
        status: (st === STATUS_ALL ? undefined : (st as "pending" | "done")) || undefined,
        assigneeId: aid.trim() || undefined,
        meetingId: mid?.trim() || undefined,
      },
    });
  };

  return (
    <div className="mb-4 flex flex-col gap-2 px-2">
      <div className="flex flex-wrap items-center gap-2">
        <DatePicker
          mode="range"
          value={{
            from: dueDateFrom ? new Date(dueDateFrom) : undefined,
            to: dueDateTo ? new Date(dueDateTo) : undefined,
          }}
          onChange={(range) => {
            const from: Date | undefined = Array.isArray(range)
              ? range[0]
              : range?.from;
            const to: Date | undefined = Array.isArray(range)
              ? range[1]
              : range?.to;
            const fromStr = from ? formatDateForInput(from) : "";
            const toStr = to ? formatDateForInput(to) : "";
            setDueDateFrom(fromStr);
            setDueDateTo(toStr);
            doNav({ dueDateFromVal: fromStr, dueDateToVal: toStr });
          }}
          placeholder="ວັນສົ່ງ (ຊ່ວງວັນທີ)"
          className="h-8 w-full sm:w-72"
        />
        <SimpleSelect
          value={status}
          onValueChange={(v) => {
            setStatus(v);
            doNav({ statusVal: v });
          }}
          options={STATUS_OPTIONS}
          placeholder="ສະຖານະ"
          aria-label="ສະຖານະ"
          className="h-8 w-full sm:w-32"
        />
        <div className="min-w-[200px] max-w-[280px] flex-1">
          <InfiniteCombobox<LookupItem>
            value={assigneeId}
            onValueChange={(v) => {
              setAssigneeId(v);
              doNav({ assigneeIdVal: v });
            }}
            queryKey={["management-meeting-actions-assignee"]}
            queryFn={(args) =>
              fetchLookupForInfinite(`${config.apiUrl}/users/lookup`, args)
            }
            preloadQueryFn={(id) =>
              hydrateLookupItem(`${config.apiUrl}/users/lookup`, id)
            }
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            placeholder="ຜູ້ຮັບຜິດຊອບ (ທັງໝົດ)"
            clearable
          />
        </div>
      </div>
    </div>
  );
}
